import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const STORAGE_KEY = 'app.cookieConsent.v1';
const DEFAULT_CATEGORIES = { necessary: true, analytics: false };
const VERSION = 1;

// Синхронное считывание согласия из localStorage до первого рендера
function readInitialConsent() {
  // SSR-guard (если вдруг будет рендериться на сервере)
  if (typeof window === 'undefined') {
    return { status: 'unknown', categories: DEFAULT_CATEGORIES };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { status: 'unknown', categories: DEFAULT_CATEGORIES };
    const parsed = JSON.parse(raw);
    // Миграция/валидация
    if (parsed.version !== VERSION || !parsed.categories) {
      return { status: 'unknown', categories: DEFAULT_CATEGORIES };
    }
    return {
      status: parsed.status || 'custom',
      categories: { necessary: true, analytics: !!parsed.categories.analytics },
    };
  } catch {
    return { status: 'unknown', categories: DEFAULT_CATEGORIES };
  }
}

const CookieConsentContext = createContext({
  status: 'unknown', // 'unknown' | 'accepted_all' | 'rejected_all' | 'custom'
  categories: DEFAULT_CATEGORIES,
  openSettings: () => {},
  acceptAll: () => {},
  rejectAll: () => {},
  saveCustom: () => {},
});

export const CookieConsentProvider = ({ children }) => {
  const init = readInitialConsent();
  const [status, setStatus] = useState(init.status);
  const [categories, setCategories] = useState(init.categories);
  const settingsOpenerRef = useRef(null);

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...next, version: VERSION })
      );
    } catch { /* ignore */ }
  }, []);

  const acceptAll = useCallback(() => {
    const next = { status: 'accepted_all', ts: Date.now(), categories: { necessary: true, analytics: true } };
    setCategories(next.categories);
    setStatus(next.status);
    persist(next);
  }, [persist]);

  const rejectAll = useCallback(() => {
    const next = { status: 'rejected_all', ts: Date.now(), categories: { necessary: true, analytics: false } };
    setCategories(next.categories);
    setStatus(next.status);
    persist(next);
  }, [persist]);

  const saveCustom = useCallback((customCategories) => {
    const next = { status: 'custom', ts: Date.now(), categories: { necessary: true, analytics: !!customCategories.analytics } };
    setCategories(next.categories);
    setStatus(next.status);
    persist(next);
  }, [persist]);

  const value = useMemo(() => ({
    status,
    categories,
    acceptAll,
    rejectAll,
    saveCustom,
    openSettings: () => settingsOpenerRef.current?.(),
    __attachSettingsOpener: (fn) => { settingsOpenerRef.current = fn; },
  }), [status, categories, acceptAll, rejectAll, saveCustom]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => useContext(CookieConsentContext);

export const AnalyticsGate = ({ onInit }) => {
  const { categories } = useCookieConsent();
  const initedRef = React.useRef(false);
  React.useEffect(() => {
    if (!initedRef.current && categories.analytics) {
      initedRef.current = true;
      onInit?.();
    }
  }, [categories.analytics, onInit]);
  return null;
};
